import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { API_BASE } from '../../utils/api';

const TicketForm = ({ onSubmit, onCancel, initialData = null }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryType: 'trip_related',
    categoryId: '',
    subcategoryId: '',
    tripId: '',
    priority: 'medium',
    tags: []
  });
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/tickets/categories?isActive=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await fetch(`${API_BASE}/tickets/categories/${categoryId}/subcategories?isActive=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSubcategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Failed to load subcategories');
    }
  };

  // Get categories by type
  const getCategoriesByType = (type) => {
    return categories.filter(cat => cat.type === type);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'categoryType') {
      // When category type changes, reset category and subcategory
      setFormData(prev => ({
        ...prev,
        categoryType: value,
        categoryId: '',
        subcategoryId: ''
      }));
      setSubcategories([]);
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'categoryId') {
      setFormData(prev => ({ ...prev, subcategoryId: '' }));
      if (value) {
        fetchSubcategories(value);
      } else {
        setSubcategories([]);
      }
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.description || !formData.categoryId || !formData.categoryType) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('categoryId', formData.categoryId);
      formDataToSend.append('categoryType', formData.categoryType);
      
      if (formData.subcategoryId) {
        formDataToSend.append('subcategoryId', formData.subcategoryId);
      }
      
      if (formData.tripId) {
        formDataToSend.append('tripId', formData.tripId);
      }
      
      formDataToSend.append('priority', formData.priority);
      
      if (formData.tags.length > 0) {
        formDataToSend.append('tags', JSON.stringify(formData.tags));
      }

      // Add attachments
      attachments.forEach(file => {
        formDataToSend.append('attachments', file);
      });

      const response = await fetch(`${API_BASE}/tickets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataToSend
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Ticket created successfully!');
        onSubmit && onSubmit(data.data);
      } else {
        toast.error(data.message || 'Error creating ticket');
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('Error creating ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-6">Create New Ticket</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category Type Selection */}
        <div>
          <label htmlFor="categoryType" className="block text-sm font-medium text-gray-700 mb-2">
            Select Category Type *
          </label>
          <select
            id="categoryType"
            name="categoryType"
            value={formData.categoryType}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="trip_related">Trip Related</option>
            <option value="app_related">App Related</option>
          </select>
          <p className="text-sm text-gray-500 mt-1">
            {formData.categoryType === 'trip_related' ? 'Issues related to a specific trip' : 'General app and system issues'}
          </p>
        </div>

        {/* Category Selection */}
        {formData.categoryType && (
          <div>
            <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              id="categoryId"
              name="categoryId"
              value={formData.categoryId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select Category</option>
              {getCategoriesByType(formData.categoryType).map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                  {cat.description ? ` - ${cat.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Subcategory */}
        {formData.categoryId && subcategories.length > 0 && (
          <div>
            <label htmlFor="subcategoryId" className="block text-sm font-medium text-gray-700 mb-2">
              Subcategory (Optional)
            </label>
            <select
              id="subcategoryId"
              name="subcategoryId"
              value={formData.subcategoryId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select Subcategory --</option>
              {subcategories.map(sub => (
                <option key={sub._id} value={sub._id}>
                  {sub.name}
                  {sub.description ? ` - ${sub.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Trip ID for trip-related tickets */}
        {formData.categoryType === 'trip_related' && (
          <div>
            <label htmlFor="tripId" className="block text-sm font-medium text-gray-700 mb-2">
              Trip ID
            </label>
            <input
              id="tripId"
              type="text"
              name="tripId"
              value={formData.tripId}
              onChange={handleInputChange}
              placeholder="Enter Trip ID (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-sm text-gray-500 mt-1">
              If this ticket is related to a specific trip, enter the Trip ID here
            </p>
          </div>
        )}

        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
            Title *
          </label>
          <input
            id="title"
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Brief title of the issue"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Description *
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Describe the issue in detail"
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        {/* Priority */}
        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-2">
            Priority Level *
          </label>
          <select
            id="priority"
            name="priority"
            value={formData.priority}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>

        {/* Attachments */}
        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 mb-2">
            Attachments (Optional)
          </label>
          <input
            id="attachments"
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xlsx"
            onChange={handleFileChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            Maximum 5 files, 10MB each. Supported: Images, PDF, DOC, DOCX, XLSX
          </p>
          {attachments.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-gray-700">Selected files:</p>
              <ul className="mt-2">
                {attachments.map((file, idx) => (
                  <li key={idx} className="text-sm text-gray-600">
                    {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Creating Ticket...' : 'Create Ticket'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TicketForm;